import type { OneDriveAccount, OneDriveScope } from '@/lib/api/onedrive.js';

/** Whether this folder can answer a read at all: both the account and the grant must be healthy. */
export function canReadOneDriveScope(
  account: OneDriveAccount,
  scope: OneDriveScope,
): boolean {
  return account.status === 'active' && scope.status === 'active';
}

/**
 * The connected folders that can still be opened. A folder whose grant has broken — the account is gone,
 * the folder was deleted, the consent was revoked — cannot answer a browse call at all.
 *
 * The way in and the picker behind it read this ONE list: the menu item counts what is here, the picker
 * lists it. Two copies of the rule drifted once already, and the menu offered a folder the picker then
 * refused to show.
 */
export function browsableOneDriveScopes(
  scopes: readonly OneDriveScope[],
): OneDriveScope[] {
  return scopes.filter((scope) => scope.status === 'active');
}

/**
 * Whether an item may be created, replaced, renamed, moved or trashed here. Never inside the
 * whole-drive grant: it opens the drive to the agent, and here it only browses.
 */
export function canMutateOneDriveItem(
  account: OneDriveAccount,
  scope: OneDriveScope,
  isScopeRoot: boolean,
): boolean {
  return (
    !isScopeRoot &&
    !scope.openGrant &&
    canReadOneDriveScope(account, scope) &&
    account.permissionLevel === 'read_write' &&
    scope.interactiveAccess === 'read_write'
  );
}

export function canMoveOneDriveItem(
  account: OneDriveAccount,
  sourceScope: OneDriveScope,
  targetScope: OneDriveScope,
  isSourceRoot: boolean,
): boolean {
  return (
    canMutateOneDriveItem(account, sourceScope, isSourceRoot) &&
    canMutateOneDriveItem(account, targetScope, false)
  );
}

/** Why a personal folder cannot be read with nobody at the screen. */
export type OneDriveAutomationBlock = 'unknown' | 'connection' | 'folder';

/**
 * Whether an automation may read this folder in the background, and what stands in the way when it may
 * not: a connection that needs reconnecting, or a folder closed to automations. The automation runs as
 * its author through their own connection, so background use — which is about other people's runs —
 * is not a gate here, as it is not for SharePoint.
 *
 * It fails closed while either half is still unread: attaching a file the agent will silently fail to
 * open is the exact outcome this check exists to prevent. A connection that cannot answer closes every
 * folder under it, so it is named first.
 */
export function oneDriveAutomationBlock(
  account: OneDriveAccount | null,
  scope: OneDriveScope | null,
): OneDriveAutomationBlock | null {
  if (account === null || scope === null) return 'unknown';
  if (account.status !== 'active') return 'connection';
  if (scope.status !== 'active' || scope.automationAccess === 'none') {
    return 'folder';
  }
  return null;
}
